<template>
  <PayPlanRowCard
    class="pay-range-version-row-card"
    :status="rowStatus"
    :state="rowState"
    :status-label="statusLabel"
    :version-code="versionCode"
    :hide-version-badge="isFirstDraft"
    :amount-formatted="amountLabel"
    :effective-from="effectiveFrom || ''"
    :effective-to="effectiveTo"
    :open-ended="!effectiveTo"
    :reference-date="referenceDate"
    :selected="selected"
    :interaction="interaction"
    :unread="unread"
    :menu-items="menuItems"
    effective-prefix="Effective from"
    entity-label="Pay range version"
    @select="emit('select', $event)"
    @hover="emit('hover', $event)"
    @open-chatter="emit('open-chatter', $event)"
    @menu-action="emit('menu-action', $event)"
  >
    <template v-if="corrected" #badges-extra>
      <PayRangeCorrectedBadge
        :corrected-at="correctedAt"
        :corrected-by-name="correctedByName"
      />
    </template>
  </PayPlanRowCard>
</template>

<script setup>
import { computed } from "vue";

import PayPlanRowCard from "@components/PayPlanRowCard/PayPlanRowCard.vue";
import PayRangeCorrectedBadge from "@components/PayRangeCorrectedBadge/PayRangeCorrectedBadge.vue";
import { PAY_PLAN_PERIOD_STATE } from "@components/PayPlanRowCard/payPlanRowCardUtils";
import { versionStatusBadge } from "@components/FairPay/payRangeBadges";
import { formatPayRangeAmount } from "@components/utils/formatPayRangeAmount";
import { PAY_RANGE_VERSION_STATE, buildPayRangeVersionMenuItems } from "./payRangeVersionMenus";

const props = defineProps({
  /** draft | in-force | voided | retired */
  status: {
    type: String,
    default: PAY_RANGE_VERSION_STATE.IN_FORCE,
    validator: (value) => Object.values(PAY_RANGE_VERSION_STATE).includes(value),
  },
  /** future | current | lapsed — ignored for voided rows (no state badge). */
  state: { type: String, default: "" },
  rangeCode: { type: String, default: "" },
  /** Draft of a never-published range: carries no version chip. */
  firstDraft: { type: Boolean, default: false },
  versionNumber: { type: [Number, String], default: null },
  minAmount: { type: [Number, String], default: null },
  maxAmount: { type: [Number, String], default: null },
  currencyCode: { type: String, default: "USD" },
  effectiveFrom: { type: [String, Date], default: null },
  effectiveTo: { type: [String, Date], default: null },
  referenceDate: { type: [String, Date], default: () => new Date() },
  /** In use by pay plans: informational only, Correct and Void stay enabled. */
  locked: { type: Boolean, default: false },
  /** Versions of a retired or archived pay range are read-only: no menu. */
  readOnly: { type: Boolean, default: false },
  selected: { type: Boolean, default: false },
  interaction: { type: String, default: "default" },
  unread: { type: Number, default: 0 },
  /** The version was corrected in place (`corrected_at` set): shows the Corrected badge. */
  corrected: { type: Boolean, default: false },
  correctedAt: { type: [String, Date], default: null },
  correctedByName: { type: String, default: "" },
});

const emit = defineEmits(["select", "hover", "open-chatter", "menu-action"]);

const isDraft = computed(() => props.status === PAY_RANGE_VERSION_STATE.DRAFT);
const isVoided = computed(() => props.status === PAY_RANGE_VERSION_STATE.VOIDED);
const isRetired = computed(() => props.status === PAY_RANGE_VERSION_STATE.RETIRED);

const rowStatus = computed(() => {
  if (isDraft.value) return "proposed";
  if (isVoided.value) return "voided";
  if (isRetired.value) return "terminated";
  return "in-force";
});

const rowState = computed(() => (isVoided.value ? PAY_PLAN_PERIOD_STATE.NONE : props.state));

const isFirstDraft = computed(() => isDraft.value && props.firstDraft);

const statusLabel = computed(() => versionStatusBadge(props.status)?.label ?? "");

const versionCode = computed(() => {
  let version = "V?";
  if (isDraft.value) version = "?";
  else if (props.versionNumber != null) version = `V${props.versionNumber}`;
  return props.rangeCode ? `${props.rangeCode}.${version}` : version;
});

const amountLabel = computed(() => {
  const prefix = `${props.currencyCode} `;
  return `${prefix}${formatPayRangeAmount(props.minAmount)} – ${formatPayRangeAmount(
    props.maxAmount
  )}`;
});

const menuItems = computed(() =>
  buildPayRangeVersionMenuItems({
    status: props.status,
    locked: props.locked,
    readOnly: props.readOnly,
  })
);
</script>
